import React, { useEffect, useMemo, useRef, useState } from 'react';

const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Performs the file to base64 operation.
 *
 * @param {*} file - file value.
 * @returns {*} The derived operation result.
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || '');
      resolve(value.includes(',') ? value.split(',').pop() : value);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Validates entry photo.
 *
 * @param {*} file - file value.
 * @returns {*} The derived operation result.
 */
export function validateEntryPhoto(file) {
  if (!file) return '';
  if (!ACCEPTED_TYPES.includes(file.type)) return 'Use a JPG, PNG, or WebP photo.';
  if (file.size > MAX_PHOTO_BYTES) return 'Choose a photo under 2 MB.';
  return '';
}

/**
 * Renders the entry photo picker experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.photoUrl - photo url value.
 * @param {*} props.draft - draft value.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @param {*} props.title - title value.
 * @param {*} props.addLabel - add label value.
 * @param {*} props.replaceLabel - replace label value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function EntryPhotoPicker({
  photoUrl = '',
  draft,
  onChange,
  title = 'Photos',
  addLabel = 'Add photo',
  replaceLabel = 'Replace photo',
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const hasDraftFile = Boolean(draft?.file);
  const shouldRemove = Boolean(draft?.remove);
  const previewUrl = draft?.previewUrl || '';
  const displayUrl = previewUrl || (!shouldRemove ? photoUrl : '');
  const hasPhoto = Boolean(displayUrl);
  const status = hasPhoto ? '1 photo selected' : addLabel;

  const buttonLabel = useMemo(() => {
    if (hasPhoto) return replaceLabel;
    return addLabel;
  }, [addLabel, hasPhoto, replaceLabel]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  /**
   * Performs the choose file operation.
   *
   * @param {*} file - file value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const chooseFile = (file) => {
    const validationError = validateEntryPhoto(file);
    setError(validationError);
    if (validationError) return;

    if (draft?.previewUrl) URL.revokeObjectURL(draft.previewUrl);
    onChange({
      file,
      previewUrl: URL.createObjectURL(file),
      remove: false,
    });
  };

  /**
   * Removes photo.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removePhoto = () => {
    if (draft?.previewUrl) URL.revokeObjectURL(draft.previewUrl);
    setError('');
    onChange({
      file: null,
      previewUrl: '',
      remove: Boolean(photoUrl),
    });
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="entry-photo-picker">
      <div className="entry-photo-copy">
        <p className="eyebrow">{title}</p>
        <strong>{status}</strong>
      </div>
      <div className="entry-photo-actions">
        {hasPhoto && (
          <div className="entry-photo-preview">
            <img src={displayUrl} alt="" />
          </div>
        )}
        <label className="btn-secondary entry-photo-file-choice">
          <span>{buttonLabel}</span>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) chooseFile(file);
            }}
          />
        </label>
        {(hasPhoto || hasDraftFile) && (
          <button type="button" className="btn-ghost danger-choice" onClick={removePhoto}>
            Remove photo
          </button>
        )}
      </div>
      {error && <p className="field-error entry-photo-error">{error}</p>}
    </div>
  );
}
